import React, { useState, useEffect, useCallback } from "react";
import { toast } from 'sonner';
import { useVenue } from "@/components/shared/VenueContext";
import hosys, { getMemToken } from "@/api/api";
import { createCleaningTask, toggleCleaningTask, getCleaningRecords, repeatRecurringTasksNextMonth, ZONE_LABELS as ZONE_LABELS_MAP } from "@/components/services/cleaningService";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, SprayCan, Calendar, CheckCircle2, Circle, Loader2, PartyPopper, Trash2, FileDown, ListChecks, RefreshCcw, AlertTriangle, Package } from "lucide-react";
import { jsPDF } from "jspdf";
import moment from "moment";
import CleaningProductsModal from "@/components/cleaning/CleaningProductsModal";

const ZONE_COLORS = {
  kitchen: "bg-orange-100 text-orange-700", bar: "bg-purple-100 text-purple-700",
  dining: "bg-blue-100 text-blue-700", bathrooms: "bg-cyan-100 text-cyan-700",
  storage: "bg-gray-100 text-gray-700", chambers: "bg-indigo-100 text-indigo-700",
  machinery: "bg-red-100 text-red-700", terrace: "bg-green-100 text-green-700",
};

const DEFAULT_TASKS = {
  kitchen: ["Netejar superfícies de treball", "Desinfectar taulers de tall", "Netejar fogons i forn", "Buidar escombraries", "Fregar el terra"],
  bar: ["Netejar barra i taulell", "Netejar màquina de cafè", "Desinfectar dosificadors", "Netejar nevera de begudes"],
  dining: ["Netejar taules i cadires", "Passar l'aspiradora", "Fregar el terra", "Netejar finestrals"],
  bathrooms: ["Netejar i desinfectar vàters", "Netejar lavabos", "Reposar paper i sabó", "Fregar el terra"],
  storage: ["Revisar ordre i neteja", "Netejar prestatgeries"],
  machinery: ["Netejar fregidores", "Netejar vitrina expositora", "Revisar i netejar campana extractora"],
};

export default function Cleaning() {
  const { currentVenue, user, loading: venueLoading } = useVenue();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(moment().format("YYYY-MM-DD"));
  const [zoneFilter, setZoneFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [toggling, setToggling] = useState(null);
  // Quick add state
  const [newZone, setNewZone] = useState("kitchen");
  const [customZone, setCustomZone] = useState("");
  const [newTask, setNewTask] = useState("");
  const [newFrequency, setNewFrequency] = useState("daily");
  const [saving, setSaving] = useState(false);
  const [showRepeatDialog, setShowRepeatDialog] = useState(false);
  const [isRepeatMonthLoading, setIsRepeatMonthLoading] = useState(false);
  const [exportingMonthly, setExportingMonthly] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [products, setProducts] = useState([]);
  const isToday = dateFilter === moment().format("YYYY-MM-DD");

  useEffect(() => {
    if (currentVenue) {
      loadRecords();
      checkPlanStatus();
      loadProducts();
    }
  }, [currentVenue, dateFilter]);

  const loadProducts = async () => {
    if (!currentVenue?.id) return;
    try {
      const items = await hosys.entities.CleaningProduct.filter({ venue_id: currentVenue.id }, "-created_at");
      setProducts(items || []);
    } catch (err) {
      console.error("Error loading products:", err);
    }
  };

  useEffect(() => {
    const today = moment(dateFilter);
    const endOfYear = today.clone().endOf("year");
    const daysUntilEndOfYear = endOfYear.diff(today, "days");

    if (daysUntilEndOfYear <= 7 && daysUntilEndOfYear >= 0 && isToday) {
      setShowRepeatDialog(true);
    }
  }, [dateFilter, isToday]);

  const checkPlanStatus = async () => {
    if (!currentVenue) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/cleaning/status/${currentVenue.id}`, {
        credentials: 'include',
        headers: getMemToken() ? { 'Authorization': `Bearer ${getMemToken()}` } : {},
      });
      if (res.ok) setPlanStatus(await res.json());
    } catch {}
  };

  const loadRecords = async () => {
    setLoading(true);
    const recs = await getCleaningRecords(currentVenue.id, dateFilter);
    setRecords(recs);
    setLoading(false);
  };

  const handleSaveTask = async () => {
    if (!newTask) return;
    const effectiveZone = newZone === "_custom" ? customZone.trim().toLowerCase().replace(/\s+/g, "_") : newZone;
    if (!effectiveZone) return;
    setSaving(true);
    await createCleaningTask(currentVenue.id, user, { zone: effectiveZone, task: newTask, frequency: newFrequency });
    setNewTask("");
    setCustomZone("");
    setNewFrequency("daily");
    setShowForm(false);
    await loadRecords();
    setSaving(false);
  };

  const addPresetTask = async (zone, task) => {
    await createCleaningTask(currentVenue.id, user, { zone, task });
    await loadRecords();
    setShowForm(false);
  };

  const toggleCompleted = async (rec) => {
    setToggling(rec.id);
    await toggleCleaningTask(rec.id, !rec.completed);
    setRecords(prev => prev.map(r => r.id === rec.id ? { ...r, completed: !r.completed } : r));
    setToggling(null);
  };

  const handleDelete = async (e, rec) => {
    e.stopPropagation();
    await hosys.entities.CleaningRecord.delete(rec.id);
    setRecords(prev => prev.filter(r => r.id !== rec.id));
    toast.success("Tasca eliminada");
  };

  const handleMarkAllDone = async () => {
    const pending = filtered.filter(r => !r.completed);
    if (pending.length === 0) return;
    setMarkingAll(true);
    try {
      const batchSize = 5;
      for (let i = 0; i < pending.length; i += batchSize) {
        const batch = pending.slice(i, i + batchSize);
        await Promise.all(batch.map(r => toggleCleaningTask(r.id, true)));
        if (i + batchSize < pending.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      setRecords(prev => prev.map(r => {
        const isPending = pending.find(p => p.id === r.id);
        return isPending ? { ...r, completed: true } : r;
      }));
    } catch (err) {
      console.error("Error marking all:", err);
      await loadRecords();
    }
    setMarkingAll(false);
  };

  const handleRenewPlan = async (fullYear = false) => {
    setRenewing(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/cleaning/renew/${currentVenue.id}`, {
        method: "POST",
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getMemToken() ? { 'Authorization': `Bearer ${getMemToken()}` } : {}),
        },
        body: JSON.stringify({ full_year: fullYear }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Pla renovat! ${data.renewed} tasques creades (${data.period}).`);
        await loadRecords();
      } else {
        toast.error(data.detail || "Error renovant el pla");
      }
    } catch (err) {
      console.error("Error renewing:", err);
      toast.error("Error de connexió");
    }
    setRenewing(false);
  };

  const appendProductsAnnex = (doc) => {
    if (!products || products.length === 0) return;
    doc.addPage();
    // Annex header
    doc.setFillColor(123, 45, 63);
    doc.rect(0, 0, 210, 24, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("ANNEX: PRODUCTES DE NETEJA UTILITZATS", 14, 12);
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.text(`Fitxes tècniques registrades: ${products.length}`, 14, 20);

    let y = 36;
    products.forEach((p, idx) => {
      if (y > 260) { doc.addPage(); y = 20; }
      // Product title
      doc.setFillColor(245, 245, 248);
      doc.rect(14, y - 5, 182, 8, "F");
      doc.setFont(undefined, "bold");
      doc.setFontSize(10);
      doc.setTextColor(123, 45, 63);
      doc.text(`${idx + 1}. ${p.name || "—"}`, 16, y);
      if (p.manufacturer) {
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(p.manufacturer, 195, y, { align: "right" });
      }
      y += 8;

      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);

      const rows = [
        ["Ús", p.usage],
        ["Dosi", p.dosage],
        ["Composició", p.composition],
        ["Riscos", p.risks],
        ["EPIs", p.epis],
        ["Primers auxilis", p.first_aid],
        ["Emmagatzematge", p.storage],
        ["Caducitat", p.expiry_date],
        ["Observacions", p.observations],
      ];

      rows.forEach(([label, val]) => {
        if (!val) return;
        if (y > 275) { doc.addPage(); y = 20; }
        doc.setFont(undefined, "bold");
        doc.setTextColor(80, 80, 80);
        doc.text(`${label}:`, 16, y);
        doc.setFont(undefined, "normal");
        doc.setTextColor(40, 40, 40);
        const lines = doc.splitTextToSize(String(val), 140);
        doc.text(lines, 50, y);
        y += (lines.length * 4) + 1;
      });

      if (p.pdf_url) {
        doc.setTextColor(120, 120, 120);
        doc.setFontSize(8);
        doc.text(`Fitxa oficial adjunta (PDF): ${p.pdf_name || "fitxa.pdf"}`, 16, y);
        y += 5;
      }
      y += 4;
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const dateLabel = moment(dateFilter).format("DD/MM/YYYY");
    const venueName = currentVenue?.name || "Local";

    // Header
    doc.setFillColor(123, 45, 63);
    doc.rect(0, 0, 210, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("REGISTRE DIARI DE NETEJA", 14, 12);
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(`${venueName}  ·  Data: ${dateLabel}`, 14, 22);

    // Summary
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(10);
    doc.text(`Total tasques: ${records.length}   Completades: ${completedCount}   Pendents: ${records.length - completedCount}   Compliment: ${pct}%`, 14, 38);

    let y = 50;
    const zones = [...new Set(records.map(r => r.zone))];

    zones.forEach(zone => {
      const zoneTasks = records.filter(r => r.zone === zone);
      const zoneLabel = ZONE_LABELS_MAP[zone] || zone;

      // Zone header
      doc.setFillColor(245, 245, 248);
      doc.rect(14, y - 5, 182, 8, "F");
      doc.setFont(undefined, "bold");
      doc.setFontSize(10);
      doc.setTextColor(123, 45, 63);
      doc.text(zoneLabel.toUpperCase(), 16, y);
      y += 8;

      zoneTasks.forEach(rec => {
        if (y > 270) { doc.addPage(); y = 20; }
        const checkMark = rec.completed ? "✓" : "○";
        doc.setFont(undefined, rec.completed ? "normal" : "normal");
        doc.setTextColor(rec.completed ? 80 : 40, rec.completed ? 80 : 40, rec.completed ? 80 : 40);
        doc.setFontSize(9);
        doc.text(checkMark, 16, y);
        doc.text(rec.task, 24, y);
        if (rec.responsible_name) {
          doc.setTextColor(150, 150, 150);
          doc.setFontSize(8);
          doc.text(rec.responsible_name, 170, y, { align: "right" });
        }
        y += 7;
      });
      y += 4;
    });

    // Signature block
    if (y > 240) { doc.addPage(); y = 20; }
    y += 6;
    doc.setDrawColor(200, 200, 200);
    doc.line(14, y, 100, y);
    doc.line(110, y, 196, y);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text("Signatura responsable", 14, y + 5);
    doc.text("Data i hora", 110, y + 5);

    // Annex: Products used
    appendProductsAnnex(doc);

    // Footer
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      doc.text(`Generat per Ho-Sys · ${moment().format("DD/MM/YYYY HH:mm")}`, 14, 290);
    }

    doc.save(`neteja_${venueName.replace(/\s/g, "_")}_${dateFilter}.pdf`);
  };

  const handleExportMonthlyPDF = async () => {
    setExportingMonthly(true);
    try {
      const monthDate = moment(dateFilter);
      const daysInMonth = monthDate.daysInMonth();
      const venueName = currentVenue?.name || "Local";
      const monthLabel = monthDate.format("MMMM YYYY");

      // Load all records for the month
      const allMonthRecords = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const date = monthDate.clone().date(d).format("YYYY-MM-DD");
        if (moment(date).isAfter(moment())) break;
        const dayRecs = await getCleaningRecords(currentVenue.id, date);
        allMonthRecords.push(...dayRecs.map(r => ({ ...r, date })));
      }

      const doc = new jsPDF();
      const totalTasks = allMonthRecords.length;
      const completedTasks = allMonthRecords.filter(r => r.completed).length;
      const monthPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

      // Header
      doc.setFillColor(123, 45, 63);
      doc.rect(0, 0, 210, 32, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.text("INFORME MENSUAL DE NETEJA", 14, 14);
      doc.setFontSize(11);
      doc.setFont(undefined, "normal");
      doc.text(`${venueName}`, 14, 22);
      doc.setFontSize(10);
      doc.text(`Periode: ${monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}`, 14, 28);

      // Summary box
      doc.setTextColor(40, 40, 40);
      doc.setFillColor(245, 245, 248);
      doc.rect(14, 38, 182, 16, "F");
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(`Total tasques: ${totalTasks}`, 20, 46);
      doc.text(`Completades: ${completedTasks}`, 75, 46);
      doc.text(`Pendents: ${totalTasks - completedTasks}`, 130, 46);
      doc.setTextColor(123, 45, 63);
      doc.text(`Compliment: ${monthPct}%`, 20, 52);
      doc.setTextColor(40, 40, 40);

      // Group by day
      let y = 64;
      const dayGroups = {};
      allMonthRecords.forEach(r => {
        if (!dayGroups[r.date]) dayGroups[r.date] = [];
        dayGroups[r.date].push(r);
      });

      const sortedDays = Object.keys(dayGroups).sort();
      sortedDays.forEach(date => {
        const dayRecs = dayGroups[date];
        const dayCompleted = dayRecs.filter(r => r.completed).length;
        const dayPct = dayRecs.length > 0 ? Math.round((dayCompleted / dayRecs.length) * 100) : 0;

        if (y > 265) { doc.addPage(); y = 20; }

        // Day header
        doc.setFillColor(123, 45, 63);
        doc.setTextColor(255, 255, 255);
        doc.rect(14, y - 5, 182, 7, "F");
        doc.setFont(undefined, "bold");
        doc.setFontSize(9);
        const dayLabel = moment(date).format("dddd D MMMM");
        doc.text(`${dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)}   —   ${dayCompleted}/${dayRecs.length} (${dayPct}%)`, 16, y);
        y += 6;
        doc.setTextColor(40, 40, 40);

        // Group by zone within day
        const zones = [...new Set(dayRecs.map(r => r.zone))];
        zones.forEach(zone => {
          const zoneTasks = dayRecs.filter(r => r.zone === zone);
          const zoneLabel = ZONE_LABELS_MAP[zone] || zone;

          if (y > 272) { doc.addPage(); y = 20; }

          doc.setFont(undefined, "bold");
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(`  ${zoneLabel}`, 16, y);
          y += 4;

          zoneTasks.forEach(rec => {
            if (y > 278) { doc.addPage(); y = 20; }
            doc.setFont(undefined, "normal");
            doc.setFontSize(8);
            doc.setTextColor(rec.completed ? 80 : 200, rec.completed ? 80 : 60, rec.completed ? 80 : 60);
            const mark = rec.completed ? "[OK]" : "[  ]";
            doc.text(`    ${mark}  ${rec.task}`, 16, y);
            if (rec.responsible_name) {
              doc.setTextColor(160, 160, 160);
              doc.text(rec.responsible_name, 190, y, { align: "right" });
            }
            y += 4;
          });
          y += 2;
        });
        y += 2;
      });

      // Annex: Products used
      appendProductsAnnex(doc);

      // Footer
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.text(`Ho-Sys · Informe mensual de neteja · ${venueName} · Generat: ${moment().format("DD/MM/YYYY HH:mm")}`, 14, 290);
        doc.text(`Pag. ${i}/${pageCount}`, 196, 290, { align: "right" });
      }

      doc.save(`neteja_mensual_${venueName.replace(/\s/g, "_")}_${monthDate.format("YYYY-MM")}.pdf`);
    } catch (err) {
      console.error("Error exporting monthly PDF:", err);
    }
    setExportingMonthly(false);
  };

  const handleRepeatNextMonth = async () => {
    setIsRepeatMonthLoading(true);
    try {
      await repeatRecurringTasksNextMonth(currentVenue.id, user);
      setShowRepeatDialog(false);
    } catch (error) {
      console.error("Error repeating tasks:", error);
    }
    setIsRepeatMonthLoading(false);
  };

  if (venueLoading) return <QuickLoader />;

  const filtered = zoneFilter === "all" ? records : records.filter(r => r.zone === zoneFilter);
  const completedCount = records.filter(r => r.completed).length;
  const pct = records.length > 0 ? Math.round((completedCount / records.length) * 100) : 0;
  const allDone = records.length > 0 && completedCount === records.length;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Control de Neteja"
        subtitle="Checklists de neteja per zones"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleExportMonthlyPDF}
              disabled={exportingMonthly}
              className="gap-2 rounded-xl"
              data-testid="export-monthly-btn"
            >
              {exportingMonthly ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Informe mensual
            </Button>
            {records.length > 0 && (
              <Button variant="outline" onClick={handleExportPDF} className="gap-2 rounded-xl" data-testid="export-daily-pdf-btn">
                <FileDown className="w-4 h-4" /> PDF diari
              </Button>
            )}
            <Button onClick={() => setShowForm(true)} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl">
              <Plus className="w-4 h-4" /> Afegir tasca
            </Button>
            <Button
              onClick={() => setShowProductsModal(true)}
              variant="outline"
              className="gap-2 rounded-xl"
              data-testid="open-products-btn"
            >
              <Package className="w-4 h-4" /> Fitxes productes
              {products.length > 0 && (
                <span className="ml-1 bg-[#7B2D3F] text-white text-xs px-1.5 py-0.5 rounded-full">{products.length}</span>
              )}
            </Button>
            <Button
              onClick={() => handleRenewPlan(true)}
              disabled={renewing}
              variant="outline"
              className="gap-2 rounded-xl border-[#7B2D3F] text-[#7B2D3F]"
              data-testid="renew-plan-header-btn"
            >
              {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              Renovar pla anual
            </Button>
          </div>
        }
      />

      {/* Progress bar */}
      <Card className={`p-4 border-0 shadow-sm ${allDone ? "bg-emerald-50" : ""}`}>
        {allDone ? (
          <div className="flex items-center gap-3 text-emerald-700">
            <PartyPopper className="w-6 h-6" />
            <span className="font-semibold">Totes les neteges completades avui!</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Progrés del dia</span>
              <span className="text-sm font-bold text-gray-900">{completedCount}/{records.length} · {pct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3 mb-3">
              <div
                className="h-3 rounded-full transition-all duration-500 bg-[#7B2D3F]"
                style={{ width: `${pct}%` }}
              />
            </div>
            {records.length > 0 && completedCount < records.length && (
              <Button
                onClick={handleMarkAllDone}
                disabled={markingAll}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 rounded-xl"
                data-testid="mark-all-done-btn"
              >
                {markingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
                Marcar tot com a fet ({records.length - completedCount} pendents)
              </Button>
            )}
          </>
        )}
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-auto" />
        </div>
        <Select value={zoneFilter} onValueChange={setZoneFilter}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Zona" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Totes les zones</SelectItem>
            {Object.entries(ZONE_LABELS_MAP).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Plan renewal banner */}
      {planStatus && planStatus.needs_renewal && records.length > 0 && (
        <Card className="p-3 border-amber-200 bg-amber-50 shadow-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                {planStatus.days_remaining <= 0
                  ? "El pla de neteja ha expirat. Renova'l per continuar."
                  : `El pla de neteja expira en ${planStatus.days_remaining} dies (últim: ${planStatus.last_date}).`
                }
              </p>
            </div>
            <Button
              onClick={() => handleRenewPlan(true)}
              disabled={renewing}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white gap-1 rounded-xl flex-shrink-0"
              data-testid="renew-plan-banner-btn"
            >
              {renewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
              Renovar any complet
            </Button>
          </div>
        </Card>
      )}

      {/* Tasks */}
      {loading ? (
        <QuickLoader />
      ) : filtered.length === 0 && records.length === 0 && isToday ? (
        <Card className="p-6 border-0 shadow-sm border-dashed border-2 border-gray-200 bg-gray-50">
          <p className="text-center text-gray-500 font-medium mb-4">Cap tasca per avui. Afegeix les tasques habituals:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {Object.entries(DEFAULT_TASKS).slice(0, 4).map(([zone, tasks]) =>
              tasks.slice(0, 2).map(task => (
                <button
                  key={`${zone}-${task}`}
                  onClick={() => addPresetTask(zone, task)}
                  className="px-3 py-2 bg-[#7B2D3F] text-white rounded-xl text-sm font-medium hover:bg-[#5B1D2F] transition-colors shadow-sm"
                >
                  + {task}
                </button>
              ))
            )}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 space-y-4">
          <SprayCan className="w-12 h-12 mx-auto text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-600">Sense tasques de neteja</h3>
          <p className="text-sm text-gray-400">No hi ha tasques per aquest dia. Pots renovar el pla o afegir tasques noves.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <Button
              onClick={() => handleRenewPlan(true)}
              disabled={renewing}
              className="bg-[#7B2D3F] hover:bg-[#5a1f2e] text-white gap-2 rounded-xl"
              data-testid="renew-plan-full-year-btn"
            >
              {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              Renovar pla anual complet
            </Button>
            <Button
              onClick={() => handleRenewPlan(false)}
              disabled={renewing}
              variant="outline"
              className="gap-2 rounded-xl"
              data-testid="renew-plan-btn"
            >
              <RefreshCcw className="w-4 h-4" />
              Renovar fins a fi d'any
            </Button>
          </div>
          {planStatus && planStatus.last_date && (
            <p className="text-xs text-gray-400">Últim dia amb tasques: {planStatus.last_date} · {planStatus.unique_tasks} tasques úniques</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Group by zone */}
          {Object.keys(ZONE_LABELS_MAP).map(zone => {
            const zoneTasks = filtered.filter(r => r.zone === zone);
            if (zoneTasks.length === 0) return null;
            return (
              <div key={zone}>
                <div className="flex items-center gap-2 px-1 mb-1">
                  <Badge variant="secondary" className={`${ZONE_COLORS[zone]} border-0 text-xs`}>
                    {ZONE_LABELS_MAP[zone]}
                  </Badge>
                  <span className="text-xs text-gray-400">
                    {zoneTasks.filter(t => t.completed).length}/{zoneTasks.length}
                  </span>
                </div>
                <div className="space-y-1.5 mb-3">
                  {zoneTasks.map(rec => (
                    <Card
                      key={rec.id}
                      className={`px-4 py-3 border-0 shadow-sm flex items-center gap-3 cursor-pointer transition-all active:scale-[0.98] ${
                        rec.completed ? "bg-emerald-50/60" : "bg-white hover:bg-gray-50"
                      }`}
                      onClick={() => !toggling && toggleCompleted(rec)}
                    >
                      <div className="flex-shrink-0">
                        {toggling === rec.id ? (
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        ) : rec.completed ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300" />
                        )}
                      </div>
                      <p className={`text-sm font-medium flex-1 ${rec.completed ? "line-through text-gray-400" : "text-gray-900"}`}>
                        {rec.task}
                      </p>
                      <button
                        onClick={(e) => handleDelete(e, rec)}
                        className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Repeat tasks dialog */}
      <Dialog open={showRepeatDialog} onOpenChange={setShowRepeatDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Repetir tasques el mes que ve?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Vols repetir totes les tasques recurrents per al mes següent?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepeatDialog(false)}>No, gràcies</Button>
            <Button onClick={handleRepeatNextMonth} disabled={isRepeatMonthLoading} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {isRepeatMonthLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Repetir tasques
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add task dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Afegir tasca de neteja</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Zona</Label>
              <Select value={newZone} onValueChange={setNewZone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ZONE_LABELS_MAP).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                  <SelectItem value="_custom">+ Nova zona personalitzada</SelectItem>
                </SelectContent>
              </Select>
              {newZone === "_custom" && (
                <Input
                  className="mt-2"
                  placeholder="Nom de la nova zona (ex: Terrassa exterior)"
                  value={customZone}
                  onChange={(e) => setCustomZone(e.target.value)}
                  autoFocus
                />
              )}
            </div>
            <div>
              <Label>Tasca</Label>
              <Input
                placeholder="Ex: Desinfectar superfícies de cuina"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveTask()}
                autoFocus
              />
            </div>
            <div>
              <Label>Freqüència</Label>
              <Select value={newFrequency} onValueChange={setNewFrequency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Per hores</SelectItem>
                  <SelectItem value="daily">Diaria</SelectItem>
                  <SelectItem value="weekly">Setmanal</SelectItem>
                  <SelectItem value="biweekly">Quinzenal</SelectItem>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="quarterly">Trimestral</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {DEFAULT_TASKS[newZone] && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Tasques habituals per {ZONE_LABELS_MAP[newZone]}:</p>
                <div className="flex flex-wrap gap-1.5">
                  {DEFAULT_TASKS[newZone].map(t => (
                    <button
                      key={t}
                      onClick={() => setNewTask(t)}
                      className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-600 hover:bg-[#7B2D3F]/10 hover:text-[#7B2D3F] transition-colors"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSaveTask} disabled={!newTask || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Afegir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CleaningProductsModal
        open={showProductsModal}
        onOpenChange={(o) => { setShowProductsModal(o); if (!o) loadProducts(); }}
        venueId={currentVenue?.id}
        user={user}
      />
    </div>
  );
}